import { useMemo } from 'react'

import type { FilterDefinition } from '@factorialco/f0-react/dist/experimental'

import {
  approvedAtFilter,
  creationTypeFilter,
  documentTypeFilter,
  internalReferenceFilter,
  reimbursableFilter,
  reportDateFilter,
  spendingAlertTypeFilter,
  statusFilter,
  useCategoryFilter,
  useCurrencyFilter,
  useEmployeeIdsFilter,
  useProjectFilter,
  useReimbursableCurrencyFilter,
  useSubcategoryFilter,
} from 'modules/expenses/components/expenses/DataCollection/filters'

import { reportedByFilter } from './_Filters/reportedByFilter'

interface UseMyExpensesFiltersProps {
  companyId: string
  canUsePerDiems: boolean
  hasReportOnBehalfFeature: boolean
  canCreateExpensesForOthers: boolean
  withinGroup?: boolean
  recommendationsEnabled?: boolean
}

export const useMyExpensesFilters = ({
  companyId,
  canUsePerDiems,
  hasReportOnBehalfFeature,
  canCreateExpensesForOthers,
  withinGroup = false,
  recommendationsEnabled,
}: UseMyExpensesFiltersProps) => {
  const projectFilter = useProjectFilter()

  const categoryFilter = useCategoryFilter({ companyId, canUsePerDiems })
  const currencyFilter = useCurrencyFilter()
  const employeeIdsFilter = useEmployeeIdsFilter(companyId)
  const reimbursableCurrencyFilter = useReimbursableCurrencyFilter()
  const subcategoryFilter = useSubcategoryFilter(companyId)

  return useMemo(() => {
    // Build filters in alphabetical order by user-facing label
    const filters: Record<string, FilterDefinition> = {
      spendingAlertType: spendingAlertTypeFilter(recommendationsEnabled), // alerts
      approvedAt: approvedAtFilter(), // approval date
      category: categoryFilter, // category
      reviewRequestAt: reportDateFilter(), // creation date
      currency: currencyFilter, // currency
      documentType: documentTypeFilter(), // document type
      creationType: creationTypeFilter(), // expense type
      internalReference: internalReferenceFilter(), // internal reference
    }

    // owner
    if (hasReportOnBehalfFeature && canCreateExpensesForOthers && !withinGroup) {
      filters.employeeIds = employeeIdsFilter
    }

    filters.reimbursable = reimbursableFilter() // payment
    filters.project = projectFilter // project
    filters.reimbursableCurrency = reimbursableCurrencyFilter // reimbursable currency

    // reported by
    if (hasReportOnBehalfFeature) {
      filters.reportedBy = reportedByFilter()
    }

    filters.status = statusFilter() // status
    filters.subcategory = subcategoryFilter // subcategory

    return filters
  }, [
    canCreateExpensesForOthers,
    categoryFilter,
    currencyFilter,
    employeeIdsFilter,
    hasReportOnBehalfFeature,
    projectFilter,
    reimbursableCurrencyFilter,
    recommendationsEnabled,
    subcategoryFilter,
    withinGroup,
  ])
}
